/**
 * AppDetailPage — view a single published App/Workspace's bundled assets.
 *
 * Renders the resolved content list with click-through to each
 * dashboard / report / semantic model. Shows publish metadata
 * (version, when, who) so users know they're looking at a frozen view.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  LayoutGrid, ArrowLeft, BarChart3, FileText, GitBranch, Loader2, Lock,
  Eye, Calendar, Layers,
} from 'lucide-react';
import { useTerms, getTerm } from '@/lib/branding';

const API = process.env.REACT_APP_BACKEND_URL;
const KIND_META = {
  dashboard: { icon: BarChart3, label: 'Dashboard', route: id => `/dashboard/${id}` },
  report:    { icon: FileText,  label: 'Report',    route: id => `/reports/view?id=${id}` },
  model:     { icon: GitBranch, label: 'Model',     route: id => `/semantic-models?model=${id}` },
  visual:    { icon: Layers,    label: 'Visual',    route: id => `/visual-library/edit?id=${id}` },
};

export default function AppDetailPage() {
  useTerms();
  const { id } = useParams();
  const navigate = useNavigate();
  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch(`${API}/api/workspaces/${id}/full`, { method: 'GET' })
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data?.detail || 'Failed to load App');
        setWs(data);
      })
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-6 flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>;
  if (!ws) return <div className="p-6 text-center text-muted-foreground">{getTerm('apps', 'singular')} not found.</div>;

  const audienceRestricted = (ws.audience?.users?.length || ws.audience?.roles?.length) > 0;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="app-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/apps')} className="mb-4 text-xs">
        <ArrowLeft className="w-3 h-3 mr-1" /> Back to {getTerm('apps')}
      </Button>

      <div className="glass-card rounded-2xl p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="p-3 rounded-xl bg-primary/10 border border-primary/20">
              <LayoutGrid className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-semibold text-foreground">{ws.name}</h1>
              <p className="text-sm text-muted-foreground mt-1">{ws.description || 'No description'}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            {ws.status === 'published' ? (
              <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/20">v{ws.published_version}</Badge>
            ) : (
              <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/20">Draft</Badge>
            )}
            {audienceRestricted && <Badge variant="outline" className="text-[10px] border-border/30"><Lock className="w-2.5 h-2.5 mr-1" /> Restricted</Badge>}
            {ws.published_at && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-1 mt-1">
                <Calendar className="w-2.5 h-2.5" /> Published {new Date(ws.published_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      </div>

      <h2 className="text-sm font-semibold text-muted-foreground mb-3">
        Bundled Content ({ws.resolved_content?.length || 0})
      </h2>
      {!ws.resolved_content || ws.resolved_content.length === 0 ? (
        <div className="glass-card rounded-2xl p-8 text-center text-sm text-muted-foreground">No content in this {getTerm('apps', 'singular')} yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ws.resolved_content.map((c, i) => {
            const meta = KIND_META[c.kind] || { icon: LayoutGrid, label: c.kind, route: () => '/' };
            const Icon = meta.icon;
            const endorsement = c.asset?.endorsement?.level;
            return (
              <div key={`${c.kind}-${c.id}-${i}`} className="glass-card rounded-2xl p-5 hover:border-primary/30 transition-all" data-testid={`app-content-${c.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2 rounded-xl bg-muted/30 border border-border/20">
                    <Icon className="w-4 h-4 text-primary" />
                  </div>
                  <Badge variant="outline" className="text-[9px] border-border/30">{meta.label}</Badge>
                </div>
                <h3 className="text-sm font-heading font-semibold text-foreground mb-1">{c.asset?.name || c.id}</h3>
                <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{c.asset?.description || ''}</p>
                {endorsement && endorsement !== 'none' && (
                  <Badge className="text-[9px] mb-3 bg-emerald-500/15 text-emerald-400 border-emerald-500/20">{endorsement}</Badge>
                )}
                <Button onClick={() => navigate(meta.route(c.id))} size="sm" className="w-full rounded-lg h-7 text-xs" data-testid={`open-content-${c.id}`}>
                  <Eye className="w-3 h-3 mr-1" /> Open
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
